import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'
// import LanguageDetector from 'i18next-browser-languagedetector'

// English and French are complete. Spanish, German and Portuguese cover
// about half of the interface today and fall back to English for the rest,
// so they are shown as beta until the bundles are finished.
export const supportedLanguages = {
  en: { code: 'en', name: 'English', flag: '🇬🇧' },
  fr: { code: 'fr', name: 'Français', flag: '🇫🇷' },
  es: { code: 'es', name: 'Español (beta)', flag: '🇪🇸' },
  de: { code: 'de', name: 'Deutsch (beta)', flag: '🇩🇪' },
  pt: { code: 'pt', name: 'Português (beta)', flag: '🇵🇹' },
}

import en from '@/locales/bundles/en'

// English ships with the app; every other language is fetched the first time
// it is needed (see ensureLanguage), which keeps ~430 KB out of the entry.
const resources: Record<string, { translation: Record<string, unknown> }> = { en }

const bundleLoaders: Record<string, () => Promise<{ default: { translation: Record<string, unknown> } }>> = {
  es: () => import('@/locales/bundles/es'),
  fr: () => import('@/locales/bundles/fr'),
  de: () => import('@/locales/bundles/de'),
  pt: () => import('@/locales/bundles/pt'),
}

const SUPPORTED_LANGUAGES = ['en', 'fr', 'es', 'de'] as const

// Priority: ?lang= from the landing page, then the language saved in this
// browser, then the browser's own language, then English.
export function resolveInitialLanguage(): string {
  const supported = (value: string | null | undefined) => {
    const code = (value || '').slice(0, 2).toLowerCase()
    return (SUPPORTED_LANGUAGES as readonly string[]).includes(code) ? code : null
  }
  try {
    const fromUrl = supported(new URLSearchParams(window.location.search).get('lang'))
    if (fromUrl) {
      localStorage.setItem('i18nextLng', fromUrl)
      return fromUrl
    }
    return supported(localStorage.getItem('i18nextLng')) || supported(navigator.language) || 'en'
  } catch {
    return 'en'
  }
}

i18n
  // .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: 'en',
    debug: import.meta.env.DEV,
    lng: resolveInitialLanguage(),
    interpolation: {
      escapeValue: false,
    },
    ns: ['translation'],
    defaultNS: 'translation',
  })

const syncDocumentLanguage = (lng: string) => {
  document.documentElement.lang = lng
  try { localStorage.setItem('i18nextLng', lng) } catch { /* storage unavailable */ }
}
syncDocumentLanguage(i18n.language)
i18n.on('languageChanged', syncDocumentLanguage)

// ---------------------------------------------------------------------------
// Supplementary keys for the School Setup → Profile & Branding tab and related
// screens. Merged after init with deep=true / overwrite=false so they fill the
// gaps WITHOUT clobbering any existing section (e.g. the large `common` /
// `schoolSetup` objects). Adding these inline above would risk a duplicate
// object key silently wiping a whole section, so we merge them here instead.
// ---------------------------------------------------------------------------
const brandingSupplement: Record<string, any> = {
  en: {
    common: { saveChanges: 'Save changes' },
    schoolManagement: { module: 'School operations' },
    schoolSetup: { branding: 'Profile & Branding', brandingTitle: 'Profile & Branding', modules: 'Modules' },
    branding: {
      uploadLogo: 'Upload logo',
      logoHint: 'PNG or SVG, transparent background recommended.',
      logoUploaded: 'Logo uploaded',
      saveToApply: 'Save changes to apply it.',
      uploadFailed: 'Logo upload failed.',
      uploading: 'Uploading…',
      schoolName: 'Display name',
      schoolNamePlaceholder: 'Shown in the sidebar and emails',
      tagline: 'Tagline',
      taglinePlaceholder: 'A short school motto',
      brandColors: 'Brand colors',
      primaryColor: 'Primary',
      secondaryColor: 'Secondary',
      accentColor: 'Accent',
      saved: 'Branding saved',
      savedDesc: 'Your school identity has been updated.',
      saveFailed: 'Failed to save branding.',
    },
  },
  es: {
    common: { saveChanges: 'Guardar cambios' },
    schoolManagement: { module: 'Operaciones escolares' },
    schoolSetup: { branding: 'Perfil y marca', brandingTitle: 'Perfil y marca', modules: 'Módulos' },
    branding: {
      uploadLogo: 'Subir logotipo',
      logoHint: 'PNG o SVG, se recomienda fondo transparente.',
      logoUploaded: 'Logotipo subido',
      saveToApply: 'Guarda los cambios para aplicarlo.',
      uploadFailed: 'Error al subir el logotipo.',
      uploading: 'Subiendo…',
      schoolName: 'Nombre para mostrar',
      schoolNamePlaceholder: 'Se muestra en la barra lateral y los correos',
      tagline: 'Lema',
      taglinePlaceholder: 'Un breve lema escolar',
      brandColors: 'Colores de marca',
      primaryColor: 'Primario',
      secondaryColor: 'Secundario',
      accentColor: 'Acento',
      saved: 'Marca guardada',
      savedDesc: 'La identidad de tu escuela ha sido actualizada.',
      saveFailed: 'Error al guardar la marca.',
    },
  },
  fr: {
    common: { saveChanges: 'Enregistrer les modifications' },
    schoolManagement: { module: 'Opérations scolaires' },
    schoolSetup: { branding: 'Profil et marque', brandingTitle: 'Profil et marque', modules: 'Modules' },
    branding: {
      uploadLogo: 'Téléverser le logo',
      logoHint: 'PNG ou SVG, fond transparent recommandé.',
      logoUploaded: 'Logo téléversé',
      saveToApply: 'Enregistrez les modifications pour l’appliquer.',
      uploadFailed: 'Échec du téléversement du logo.',
      uploading: 'Téléversement…',
      schoolName: 'Nom affiché',
      schoolNamePlaceholder: 'Affiché dans la barre latérale et les e-mails',
      tagline: 'Slogan',
      taglinePlaceholder: 'Une courte devise de l’école',
      brandColors: 'Couleurs de la marque',
      primaryColor: 'Primaire',
      secondaryColor: 'Secondaire',
      accentColor: 'Accent',
      saved: 'Marque enregistrée',
      savedDesc: 'L’identité de votre école a été mise à jour.',
      saveFailed: 'Échec de l’enregistrement de la marque.',
    },
  },
  de: {
    common: { saveChanges: 'Änderungen speichern' },
    schoolManagement: { module: 'Schulbetrieb' },
    schoolSetup: { branding: 'Profil & Branding', brandingTitle: 'Profil & Branding', modules: 'Module' },
    branding: {
      uploadLogo: 'Logo hochladen',
      logoHint: 'PNG oder SVG, transparenter Hintergrund empfohlen.',
      logoUploaded: 'Logo hochgeladen',
      saveToApply: 'Änderungen speichern, um es anzuwenden.',
      uploadFailed: 'Logo-Upload fehlgeschlagen.',
      uploading: 'Wird hochgeladen…',
      schoolName: 'Anzeigename',
      schoolNamePlaceholder: 'Wird in der Seitenleiste und in E-Mails angezeigt',
      tagline: 'Slogan',
      taglinePlaceholder: 'Ein kurzes Schulmotto',
      brandColors: 'Markenfarben',
      primaryColor: 'Primär',
      secondaryColor: 'Sekundär',
      accentColor: 'Akzent',
      saved: 'Branding gespeichert',
      savedDesc: 'Die Identität Ihrer Schule wurde aktualisiert.',
      saveFailed: 'Branding konnte nicht gespeichert werden.',
    },
  },
  pt: {
    common: { saveChanges: 'Guardar alterações' },
    schoolManagement: { module: 'Operações escolares' },
    schoolSetup: { branding: 'Perfil e marca', brandingTitle: 'Perfil e marca', modules: 'Módulos' },
    branding: {
      uploadLogo: 'Carregar logótipo',
      logoHint: 'PNG ou SVG, fundo transparente recomendado.',
      logoUploaded: 'Logótipo carregado',
      saveToApply: 'Guarde as alterações para aplicar.',
      uploadFailed: 'Falha ao carregar o logótipo.',
      uploading: 'A carregar…',
      schoolName: 'Nome a apresentar',
      schoolNamePlaceholder: 'Mostrado na barra lateral e nos e-mails',
      tagline: 'Lema',
      taglinePlaceholder: 'Um breve lema da escola',
      brandColors: 'Cores da marca',
      primaryColor: 'Primária',
      secondaryColor: 'Secundária',
      accentColor: 'Destaque',
      saved: 'Marca guardada',
      savedDesc: 'A identidade da sua escola foi atualizada.',
      saveFailed: 'Falha ao guardar a marca.',
    },
  },
}

const applyBrandingSupplement = (lng: string) => {
  const res = brandingSupplement[lng]
  // deep merge, do NOT overwrite existing keys
  if (res) i18n.addResourceBundle(lng, 'translation', res, true, false)
}
applyBrandingSupplement('en')

const pendingLoads: Record<string, Promise<void>> = {}

/** Make sure a language's bundle is registered before it is displayed. */
export function ensureLanguage(lng: string): Promise<void> {
  const code = (lng || 'en').slice(0, 2).toLowerCase()
  if (i18n.hasResourceBundle(code, 'translation')) return Promise.resolve()
  const load = bundleLoaders[code]
  if (!load) return Promise.resolve()
  pendingLoads[code] ||= load()
    .then(m => {
      i18n.addResourceBundle(code, 'translation', m.default.translation, true, true)
      applyBrandingSupplement(code)
    })
    .catch(err => {
      delete pendingLoads[code]
      console.error(`[i18n] Could not load the ${code} bundle`, err)
    })
  return pendingLoads[code]
}

// Existing callers use i18n.changeLanguage directly; load the bundle first so
// the switch never shows keys or English while the file is in flight.
const originalChangeLanguage = i18n.changeLanguage.bind(i18n)
i18n.changeLanguage = (async (lng?: string, callback?: Parameters<typeof originalChangeLanguage>[1]) => {
  if (lng) await ensureLanguage(lng)
  return originalChangeLanguage(lng, callback)
}) as typeof i18n.changeLanguage

// Debug: Check if resources are loaded
export default i18n
